import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiPlus, HiTrash, HiX } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { patientsApi, protocolsApi } from '../../services/api';
import { formatDateDMY } from '../../utils/dateFormat';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

const PatientProtocols = () => {
  const { user } = useAuth();
  const { isAdmin, isTherapist } = usePermissions();
  const isClinicAdmin = user?.role === 'clinic_admin';
  const canManage = isAdmin || isClinicAdmin;
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [protocolPatients, setProtocolPatients] = useState([]);
  const [showProtocolModal, setShowProtocolModal] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [protocolForm, setProtocolForm] = useState({
    patient_id: '',
    protocol_text: '',
  });
  const [savingProtocol, setSavingProtocol] = useState(false);
  const [deleteProtocolModal, setDeleteProtocolModal] = useState({ open: false, protocol: null });

  const fetchProtocols = async () => {
    setLoading(true);
    try {
      const response = await protocolsApi.getAll();
      setProtocols(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch protocols');
    } finally {
      setLoading(false);
    }
  };

  const fetchProtocolPatients = async () => {
    try {
      const response = await patientsApi.getAll({ status: 'active', limit: 200 });
      setProtocolPatients(response.data.data?.patients || []);
    } catch (error) {
      setProtocolPatients([]);
    }
  };

  useEffect(() => {
    fetchProtocols();
    if (canManage) {
      fetchProtocolPatients();
    }
  }, [canManage]);

  /** Group protocols by patient for compact mobile cards (API already scopes therapists to assigned patients). */
  const protocolsByPatient = useMemo(() => {
    const map = new Map();
    for (const p of protocols) {
      const key = p.patient_id;
      if (!map.has(key)) {
        map.set(key, {
          patient_id: key,
          patient_name: p.patient_name,
          patient_code: p.patient_code,
          items: [],
        });
      }
      map.get(key).items.push(p);
    }
    for (const g of map.values()) {
      g.items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.patient_name || '').localeCompare(String(b.patient_name || ''), undefined, { sensitivity: 'base' })
    );
  }, [protocols]);

  const openCreateProtocolModal = () => {
    setEditingProtocol(null);
    setProtocolForm({ patient_id: '', protocol_text: '' });
    setShowProtocolModal(true);
  };

  const openEditProtocolModal = (protocol) => {
    setEditingProtocol(protocol);
    setProtocolForm({
      patient_id: String(protocol.patient_id || ''),
      protocol_text: protocol.protocol_text || '',
    });
    setShowProtocolModal(true);
  };

  const closeProtocolModal = () => {
    setShowProtocolModal(false);
    setEditingProtocol(null);
    setProtocolForm({ patient_id: '', protocol_text: '' });
    setSavingProtocol(false);
  };

  const handleProtocolSubmit = async (e) => {
    e.preventDefault();
    if (!protocolForm.patient_id || !protocolForm.protocol_text.trim()) {
      toast.error('Patient and protocol text are required');
      return;
    }
    setSavingProtocol(true);
    try {
      const payload = {
        patient_id: Number(protocolForm.patient_id),
        protocol_text: protocolForm.protocol_text.trim(),
      };
      if (editingProtocol) {
        await protocolsApi.update(editingProtocol.id, payload);
        toast.success('Protocol updated');
      } else {
        await protocolsApi.create(payload);
        toast.success('Protocol added');
      }
      closeProtocolModal();
      await fetchProtocols();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save protocol');
      setSavingProtocol(false);
    }
  };

  const openDeleteProtocolModal = (protocol) => {
    setDeleteProtocolModal({ open: true, protocol });
  };

  const closeDeleteProtocolModal = () => {
    setDeleteProtocolModal({ open: false, protocol: null });
  };

  const handleDeleteProtocol = async () => {
    if (!deleteProtocolModal.protocol) return;
    try {
      await protocolsApi.delete(deleteProtocolModal.protocol.id);
      toast.success('Protocol deleted');
      closeDeleteProtocolModal();
      await fetchProtocols();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete protocol');
    }
  };

  return (
    <div className="animate-fade-in space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold text-gray-900 md:text-2xl">Patient Protocols</h1>
          <p className="mt-1 text-sm text-gray-500 md:text-base">
            {isTherapist
              ? 'Read-only notes for patients assigned to you.'
              : 'Add multiple protocol notes per patient'}
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={openCreateProtocolModal}
            className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <HiPlus className="h-5 w-5" />
            Add Protocol
          </button>
        ) : null}
      </div>

      <div className="card overflow-hidden p-0 md:p-0">
        {loading ? (
          <div className="flex h-28 items-center justify-center md:h-32">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary-500 border-t-transparent md:h-8 md:w-8" />
          </div>
        ) : protocols.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500 md:py-10">
            {isTherapist ? 'No protocols for your assigned patients yet.' : 'No protocols added yet.'}
          </p>
        ) : (
          <>
            {/* Mobile / tablet: grouped cards */}
            <div className="divide-y divide-gray-100 lg:hidden">
              {protocolsByPatient.map((group) => (
                <div key={group.patient_id} className="px-3 py-3 sm:px-4">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900">{group.patient_name}</p>
                      <p className="truncate text-xs text-primary-600">{group.patient_code}</p>
                    </div>
                    {isTherapist && group.patient_id ? (
                      <Link
                        to={`/patients/${group.patient_id}`}
                        className="shrink-0 text-xs font-semibold text-primary-600 hover:text-primary-700"
                      >
                        Patient →
                      </Link>
                    ) : null}
                  </div>
                  <ul className="space-y-3">
                    {group.items.map((protocol, i) => (
                      <li
                        key={protocol.id}
                        className="rounded-xl border border-gray-100 bg-gray-50/80 p-3 text-sm text-gray-800"
                      >
                        <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                          <span>{formatDateDMY(protocol.created_at)}</span>
                          <span>·</span>
                          <span className="truncate">{protocol.created_by_name}</span>
                          {i === 0 ? (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                              Latest
                            </span>
                          ) : (
                            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                              Earlier
                            </span>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap text-[15px] leading-snug text-gray-900">
                          {protocol.protocol_text}
                        </p>
                        {canManage ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => openEditProtocolModal(protocol)}
                              className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteProtocolModal(protocol)}
                              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              <HiTrash className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden overflow-x-auto lg:block">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="px-3 py-2 font-semibold">Patient</th>
                    <th className="px-3 py-2 font-semibold">Protocol</th>
                    <th className="px-3 py-2 font-semibold">Created By</th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    {canManage ? <th className="px-3 py-2 text-right font-semibold">Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {protocols.map((protocol, idx) => {
                    const isNewestForPatient =
                      protocols.findIndex((p) => p.patient_id === protocol.patient_id) === idx;
                    return (
                      <tr key={protocol.id} className="border-b border-gray-100">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 font-medium text-gray-900">
                            {protocol.patient_name}
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                isNewestForPatient ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {isNewestForPatient ? 'New' : 'Old'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">{protocol.patient_code}</div>
                        </td>
                        <td className="whitespace-pre-wrap px-3 py-2 text-gray-700">{protocol.protocol_text}</td>
                        <td className="px-3 py-2 text-gray-700">{protocol.created_by_name}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">{formatDateDMY(protocol.created_at)}</td>
                        {canManage ? (
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEditProtocolModal(protocol)}
                                className="rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => openDeleteProtocolModal(protocol)}
                                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                <HiTrash className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showProtocolModal && canManage && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-h-[90vh] sm:rounded-xl sm:max-w-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 sm:p-5">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingProtocol ? 'Edit Protocol' : 'Add Protocol'}
              </h3>
              <button type="button" onClick={closeProtocolModal} className="text-gray-400 hover:text-gray-600">
                <HiX className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleProtocolSubmit} className="space-y-4 p-4 sm:p-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Patient</label>
                <select
                  value={protocolForm.patient_id}
                  onChange={(e) => setProtocolForm((f) => ({ ...f, patient_id: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select patient</option>
                  {protocolPatients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.full_name} ({patient.patient_code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Protocol</label>
                <textarea
                  rows={6}
                  value={protocolForm.protocol_text}
                  onChange={(e) => setProtocolForm((f) => ({ ...f, protocol_text: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter patient protocol..."
                  required
                />
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  onClick={closeProtocolModal}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 hover:bg-gray-50 sm:py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingProtocol}
                  className="rounded-lg bg-primary-600 px-4 py-2.5 text-white hover:bg-primary-700 disabled:opacity-60 sm:py-2"
                >
                  {savingProtocol ? 'Saving...' : editingProtocol ? 'Update Protocol' : 'Save Protocol'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteProtocolModal.open && canManage && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-2xl bg-white shadow-xl sm:rounded-xl">
            <div className="border-b border-gray-200 p-4 sm:p-5">
              <h3 className="text-lg font-semibold text-gray-900">Delete Protocol</h3>
              <p className="mt-1 text-sm text-gray-500">
                Are you sure you want to delete this protocol for{' '}
                <span className="font-medium">{deleteProtocolModal.protocol?.patient_name}</span>?
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end sm:gap-3 sm:p-5">
              <button
                type="button"
                onClick={closeDeleteProtocolModal}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteProtocol}
                className="rounded-lg bg-red-600 px-4 py-2.5 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientProtocols;
